import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    ComponentDefinition,
    IdentifierType,
    Group,
    ScriptTestMode,
    AlarmList,
    StateDto,
    AlarmLevel,
    IValueViewModel,
    TagStringValueDto,
    AggregationMode,
    DigitalSetSimpleDictionary
} from '../../models';
import {
    IIdentifierVmService,
    IDateRangeService,
    IStateService,
    IValuesService,
    IStringValuesService,
    IDigitalSetService,
    IValueFormattingService
} from '../../services';
import { guidEmpty, refreshClick, momentFormat, IAlarmHelper } from '../../common/';
import { viewModel } from './viewModel';

class IdentifiersTableWithDataController implements angular.IComponentController {
    alarmLevels = Object.keys(AlarmLevel);
    alarms: AlarmList;
    alarmsCount: { [guid: string]: { [level: string]: { count: number; color: string } } };
    date: string;
    digitalSets: DigitalSetSimpleDictionary;
    group: Group;
    guidEmpty = guidEmpty;
    identifiers: viewModel[];
    identifierType = IdentifierType;
    isLoading: boolean;
    isSelectionPrevented: boolean;
    scriptMode: ScriptTestMode;
    selectedItem: IValueViewModel;
    selectedRowIndex: number;
    states: StateDto[];
    stringValues: TagStringValueDto[];
    translateData: any;

    static controllerId = 'IdentifiersTableWithData';
    static $inject = [
        '$scope',
        '$translate',
        '$q',
        'identifierVmService',
        'dateRangeService',
        'stateService',
        'valuesService',
        'alarmHelper',
        'stringValuesService',
        'digitalSetService',
        'valueFormattingService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _$q: angular.IQService,
        private _identifierVmService: IIdentifierVmService,
        private _dateRangeService: IDateRangeService,
        private _stateService: IStateService,
        private _valuesService: IValuesService,
        private _alarmHelper: IAlarmHelper,
        private _stringValuesService: IStringValuesService,
        private _digitalSetService: IDigitalSetService,
        private _valueFormattingService: IValueFormattingService
    ) {}

    $onInit() {
        this.identifiers = [];

        // odd trick to get only the second half of keys which are the numerical ones
        this.alarmLevels = this.alarmLevels.slice(this.alarmLevels.length / 2);
        this._$q.all([this._stringValuesService.getAll(), this._digitalSetService.getAll()]).then((data) => {
            this.stringValues = data[0];
            const digitalSets: DigitalSetSimpleDictionary = {};
            data[1].forEach((ds) => {
                const states: { [code: number]: string } = {};
                ds.stateCollection.forEach((s) => (states[s.code] = s.name));
                digitalSets[ds.guid] = states;
            });
            this.digitalSets = digitalSets;
            this._$scope.$watch(
                () => this.group,
                () => {
                    if (this.group) {
                        this.loadData();
                    }
                }
            );
            this._$scope.$on(refreshClick, (event, data) => this.refresh(!!data.force));
            this._$scope.$watch(
                () => this.scriptMode,
                () => this.refresh()
            );

            this._$scope.$watch(
                () => this.alarms,
                () => {
                    this.buildViewModel();
                },
                true
            );
        });

        this._$scope.$watch(
            () => this.selectedItem,
            () => this.reselectIfNeeded()
        );

        this._$scope.$watch(
            () => this.selectedRowIndex,
            (newValue, oldValue) => {
                if (newValue === undefined && oldValue !== undefined) {
                    this.selectedRowIndex = oldValue;
                }
            }
        );
    }

    loadData() {
        this.isLoading = true;
        const identifierCollection = _.sortBy(this.group.identifierCollection, ['sortingOrder', 'name']);
        const viewModels: { [guid: string]: any } = {};
        this._identifierVmService
            .getAllViewModels()
            .then((list) => {
                list.forEach((e) => (viewModels[e.guid] = e));

                const identifiers: viewModel[] = identifierCollection.map((i) => {
                    const vm = viewModels[i.guid];
                    (vm.defaultValue = i.defaultValue ? i.defaultValue : 1), (vm.selectedValue = i.defaultValue);

                    if (i.tagDefinitionGuid !== this.guidEmpty) {
                        const tag = viewModels[i.tagDefinitionGuid];
                        if (tag) {
                            vm.tagDefinitionGuid = tag.guid;
                            vm.tagName = tag.name;
                            vm.tag = tag;
                            vm.defaultValue = tag.defaultValue ? tag.defaultValue : 1;
                        }
                    }

                    if (i.linkedGuid !== this.guidEmpty) {
                        vm.linked = viewModels[i.linkedGuid];
                    }
                    return vm;
                });

                // compile fix
                //identifiers = (identifiers);

                return identifiers;
            })
            .then((identifiers) => {
                // assign to view
                this.identifiers = identifiers;
                this.reselectIfNeeded();
                this.isLoading = false;
                this.refresh();
            })
            .catch((reason) => {
                console.error(reason);
                this.isLoading = false;
            });
    }

    reselectIfNeeded() {
        // reselect if needed
        if (this.selectedItem) {
            this.selectedRowIndex = _.indexOf(
                this.identifiers,
                _.find(this.identifiers, (i) => i.guid === this.selectedItem.guid)
            );
        }
    }

    setClickedRow(item, index: number, $event: angular.IAngularEvent): void {
        if (!this.isSelectionPrevented) {
            this.selectedRowIndex = index;
            this.selectedItem = item;
            $event.preventDefault();
        }
    }

    getIcon(icon: string) {
        return this._stateService.getIcon(icon);
    }

    private refresh(force?: boolean) {
        const self = this;
        this.isLoading = true;
        const selectedDate =
            this.scriptMode === ScriptTestMode.EndDateValue ? (this._dateRangeService.endDate() as string) : (this._dateRangeService.beginDate() as string);
        const date = moment(selectedDate).format(momentFormat);

        this.translateData = this.scriptMode === ScriptTestMode.DefaultValue ? 'Default values' : { computeDate: date };
        this._valuesService
            .getValuesAndStatuses(
                this.identifiers.map((i) => i.guid),
                selectedDate,
                force
            )
            .then((data) => {
                const work = angular.copy(this.identifiers);
                work.forEach((i) => {
                    const found = _.find(data, (d) => d.guid === i.guid);
                    if (found) {
                        i.value = found.value;
                        i.status = found.status;
                        i.selectedValue = self.getSelectedValue(i.value, i.defaultValue);
                        i.statusName = !!found.statusNames ? found.statusNames.map((d) => this._$translate.instant(d)).join('\n') : found.status;
                        if (this.stringValues && this.digitalSets) {
                            i.formattedValue = this._valueFormattingService.formatValue(
                                { ...found, value: found.value !== null && found.value !== undefined ? found.value : undefined, valueType: 'average'},
                                i,
                                this.digitalSets,
                                AggregationMode.Last,
                                2,
                                undefined,
                                this.stringValues
                            );
                        } else {
                            i.formattedValue = i.value?.toFixed(2);
                        }
                    }
                });
                this.identifiers = work;
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private getSelectedValue(value: number, defaultValue: number): number {
        let returnValue: number;
        switch (this.scriptMode) {
            case ScriptTestMode.MixedValue:
                returnValue = value ? value : defaultValue;
                break;
            case ScriptTestMode.DefaultValue:
                returnValue = defaultValue;
                break;
            case ScriptTestMode.BeginDateValue:
            case ScriptTestMode.EndDateValue:
            default:
                returnValue = value;
                break;
        }
        return returnValue;
    }

    private buildViewModel() {
        this.alarmsCount = {};
        if (this.alarms) {
            for (const guid of Object.keys(this.alarms)) {
                this.alarmsCount[guid] = {};
                this.alarmLevels.forEach((level) => {
                    const current = { color: this._alarmHelper.alarmLineLevelColor(AlarmLevel[level]), count: 0 };
                    if (this.alarms[guid]) {
                        current.count = this.alarms[guid].filter((a) => AlarmLevel[a.level] === level).length;
                    }
                    this.alarmsCount[guid][level] = current;
                });
            }
        }
    }
}

export const wtIdentifiersTableWithData: ComponentDefinition = {
    componentId: 'wtIdentifiersTableWithData',
    controller: IdentifiersTableWithDataController,
    template: require('./identifiersTableWithData.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '=',
        group: '=',
        isSelectionPrevented: '<',
        selectedItem: '=?',
        scriptMode: '=',
        states: '=',
        alarms: '='
    }
};
